import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { Prompt } from 'lib/types/generated-types'
import { toast } from 'react-hot-toast'
import { useAuth } from 'util/auth'

export function usePrompts({ contentPackTypeId, includeArchived = true }) {
  return useQuery<Prompt[]>(
    ['prompts', { contentPackTypeId }],
    async () => {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('content_pack_type_id', contentPackTypeId)
        .eq('is_content_pack_prompt', true)
        .not('archived', 'is', includeArchived)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data
    },
    {
      enabled: !!contentPackTypeId,
      keepPreviousData: true,
    }
  )
}

export function useUnassignedPrompts() {
  return useQuery(['individual_prompts'], async () => {
    const { data, error } = await supabase
      .from('prompts')
      .select('*')
      .is('is_universal', true)
      .not('archived', 'is', true)
    if (error) throw error
    return data
  })
}

export function usePrompt({ promptId }) {
  return useQuery(
    ['project_member', { promptId }],
    async () => {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('id', promptId)
      if (error) throw error
      return data
    },
    {
      enabled: !!promptId,
    }
  )
}

export const useDeletePrompt = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ prompt_id }: { prompt_id: number }) => {
      const { data, error } = await supabase
        .from('prompts')
        .update({ archived: true })
        .eq('id', prompt_id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    {
      onSuccess: async (data) => {
        const contentPackTypeId = data?.content_pack_type_id
        queryClient.invalidateQueries([
          'content_pack_types',
          { contentPackTypeId },
        ])
        queryClient.invalidateQueries(['prompts', { contentPackTypeId }])
      },
    }
  )
}

export const useCreatePrompt = () => {
  const queryClient = useQueryClient()
  const auth = useAuth()
  const userId = auth.user ? auth.user.id : ''

  return useMutation(
    async (prompt: Partial<Prompt>) => {
      if (
        !prompt.name ||
        !prompt.prompt_text ||
        !prompt.project_id ||
        !prompt.content_pack_type_id
      ) {
        toast.error(
          'Prompt name, prompt text, and project id, and content pack type id are required'
        )
        return
      }
      const { data, error } = await supabase
        .from('prompts')
        .insert({
          ...prompt,
          name: prompt.name || '',
          prompt_text: prompt.prompt_text || '',
          created_by: userId,
        })
        .select('*')
        .single()
      return data
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries(['content_pack_types'])
        queryClient.invalidateQueries(['prompts'])
        queryClient.invalidateQueries(['content_pack_type'])
      },
    }
  )
}

export const useUpdatePrompt = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ prompt }: { prompt: Prompt }) => {
      if (prompt.is_universal) {
        return null
      }
      const { data, error } = await supabase
        .from('prompts')
        .upsert(prompt, { onConflict: 'id' })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    {
      onSuccess: async (data) => {
        if (!data) return
        const contentPackTypeId = data?.content_pack_type_id
        queryClient.invalidateQueries([
          'content_pack_types',
          { contentPackTypeId },
        ])
        queryClient.invalidateQueries(['prompts', { contentPackTypeId }])
        queryClient.invalidateQueries(['prompt', { prompt_id: data.id }])
      },
    }
  )
}
